import { useState, type CSSProperties, type ReactNode } from 'react';
import { AuthorChip } from './Avatar';
import { Checkbox } from './Controls';
import { IconComment, IconHeart } from './icons';
import { VisBadge, type Visibility } from './Visibility';

/**
 * A cover cell. The prototype paints CSS gradients; the app paints
 * thumbnails. `src` is either a URL (drawn as an <img>, object-fit cover,
 * placeholder grey behind it while it loads) or a CSS background value.
 * A thumbnail that does not load (a study with no image yet, a photo with no
 * preview) leaves the plain placeholder, never the browser's broken image.
 */
function isUrl(s: string | undefined): boolean {
  return !!s && (s.startsWith('http') || s.startsWith('/') || s.startsWith('blob:') || s.startsWith('data:'));
}

export function Cover({ src, fallback = '#f2f2f0', style, radius, className }: { src?: string; fallback?: string; style?: CSSProperties; radius?: number; className?: string }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (isUrl(src)) {
    return (
      <div className={className} style={{ background: fallback, overflow: 'hidden', borderRadius: radius, ...style }}>
        {failed === src ? null : (
          <img src={src} alt="" loading="lazy" draggable={false} onError={() => setFailed(src!)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        )}
      </div>
    );
  }
  return <div className={className} style={{ background: src || fallback, borderRadius: radius, ...style }} />;
}

/** The 11px mono count pill at the bottom left of a thumb. */
function CountPill({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 10,
        left: 10,
        height: 22,
        padding: '0 8px',
        borderRadius: 5,
        background: 'rgba(255,255,255,.9)',
        fontSize: 11,
        fontWeight: 500,
        lineHeight: '22px',
        whiteSpace: 'nowrap',
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Mosaic Thumb.dc.html: 4:3, radius 10, three cells (3fr 2fr columns, the
 * first spanning both rows), 2px gaps, ring on hover, count pill, visibility
 * badge unless `vis` is 'none'.
 */
export function MosaicThumb({ c1, c2, c3, count, vis, selected, onToggle, selectLabel, className, style }: { c1?: string; c2?: string; c3?: string; count: number; vis: Visibility | 'none'; selected?: boolean; selectLabel?: string; onToggle?: (e: React.MouseEvent | React.KeyboardEvent) => void; className?: string; style?: CSSProperties }) {
  return (
    <div
      className={['tr-hover-ring', className].filter(Boolean).join(' ')}
      style={{
        position: 'relative',
        aspectRatio: '4 / 3',
        borderRadius: 10,
        // The selected ring sits OUTSIDE the tile, like the Stack Thumb's, so
        // it is not clipped by the overflow that keeps the mosaic square.
        boxShadow: selected ? '0 0 0 2px #fff, 0 0 0 4px #0f0f10' : undefined,
        overflow: 'hidden',
        border: '1px solid #ebebea',
        background: '#fff',
        display: 'grid',
        gridTemplateColumns: '3fr 2fr',
        gridTemplateRows: '1fr 1fr',
        gap: 2,
        ...style,
      }}
    >
      <Cover src={c1} fallback="#f2f2f0" style={{ gridRow: '1 / 3' }} />
      <Cover src={c2} fallback="#e8e8e6" />
      <Cover src={c3} fallback="#e0e0de" />
      {vis !== 'none' ? <VisBadge vis={vis} /> : null}
      <CountPill>
        {count} {count === 1 ? 'study' : 'studies'}
      </CountPill>
      {onToggle ? (
        <div style={{ position: 'absolute', top: 10, left: 10 }}>
          <Checkbox checked={!!selected} onToggle={onToggle} size={20} raised label={selectLabel} />
        </div>
      ) : null}
    </div>
  );
}

/** The small 72×48 / 56×40 mosaic used in list rows and the trending list. */
export function MosaicMini({ c1, c2, c3, w = 72, h = 48 }: { c1?: string; c2?: string; c3?: string; w?: number; h?: number }) {
  return (
    <div style={{ width: w, height: h, borderRadius: 6, overflow: 'hidden', border: '1px solid #ebebea', display: 'grid', gridTemplateColumns: '3fr 2fr', gridTemplateRows: '1fr 1fr', gap: 1, background: '#fff', flex: 'none' }}>
      <Cover src={c1} fallback="#f2f2f0" style={{ gridRow: '1 / 3' }} />
      <Cover src={c2} fallback="#e8e8e6" />
      <Cover src={c3} fallback="#e0e0de" />
    </div>
  );
}

function HeartCount({ likes, liked, onToggle }: { likes: number; liked?: boolean; onToggle?: () => void }) {
  return (
    <div
      title={onToggle ? (liked ? 'Remove from saved' : 'Save') : undefined}
      onClick={
        onToggle
          ? (e) => {
              e.stopPropagation();
              onToggle();
            }
          : undefined
      }
      style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 12.5, color: '#6b6b70', cursor: onToggle ? 'pointer' : undefined }}
    >
      <IconHeart size={13} fill={liked ? '#d9342b' : 'none'} color={liked ? '#d9342b' : 'currentColor'} />
      {likes}
    </div>
  );
}

function CommentCount({ count }: { count: number }) {
  if (!count) return null;
  return (
    <div title={`${count} ${count === 1 ? 'comment' : 'comments'}`} style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 12.5, color: '#6b6b70' }}>
      <IconComment size={13} />
      {count}
    </div>
  );
}

/**
 * Mosaic Card.dc.html: the only way a collection card is rendered
 * (PRODUCT_RULES). Title 14/500, description clamped to 2 lines, meta 12px,
 * optional Author Chip, discussion count and likes on the right.
 */
export function MosaicCard({
  c1,
  c2,
  c3,
  count,
  vis,
  title,
  desc,
  meta,
  author,
  likes,
  liked,
  discussion = 0,
  selected,
  onToggle,
  onOpen,
  onToggleLike,
}: {
  c1?: string;
  c2?: string;
  c3?: string;
  count: number;
  vis: Visibility | 'none';
  title: string;
  desc?: string;
  meta?: string;
  author?: string;
  likes: number;
  liked?: boolean;
  discussion?: number;
  selected?: boolean;
  onToggle?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  onOpen: () => void;
  onToggleLike?: () => void;
}) {
  return (
    <div onClick={onOpen} style={{ display: 'flex', flexDirection: 'column', gap: 12, cursor: 'pointer' }}>
      <MosaicThumb c1={c1} c2={c2} c3={c3} count={count} vis={vis} selected={selected} onToggle={onToggle} selectLabel={`Select ${title}`} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
          <div className="tr-ellipsis" style={{ fontSize: 14, fontWeight: 500 }}>{title}</div>
          <div className="tr-clamp-2" style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.4 }}>{desc}</div>
          <div style={{ fontSize: 12, color: '#8a8a8f' }}>{meta}</div>
          {author ? (
            <div style={{ marginTop: 3 }}>
              <AuthorChip author={author} />
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingTop: 2, flex: 'none' }}>
          <CommentCount count={discussion} />
          <HeartCount likes={likes} liked={liked} onToggle={onToggleLike} />
        </div>
      </div>
    </div>
  );
}

/**
 * Stack Thumb.dc.html: the only way a study card's image is rendered. Two
 * greyed "sheets" behind, the cover in front (radius 10), ring on hover or
 * when selected, count pill, visibility badge, optional 20px checkbox.
 */
export function StackThumb({
  cover,
  count,
  vis,
  selected,
  onToggle,
  selectLabel,
  style,
}: {
  cover?: string;
  count: number;
  vis: Visibility | 'none';
  selected?: boolean;
  onToggle?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  /** The checkbox's name for a screen reader, e.g. "Select IDC-04". */
  selectLabel?: string;
  style?: CSSProperties;
}) {
  return (
    <div style={{ position: 'relative', aspectRatio: '4 / 3', marginTop: 14, ...style }}>
      <div style={{ position: 'absolute', left: 16, right: 16, top: -14, height: '70%', borderRadius: 8, background: '#ececea', border: '1px solid #ebebea' }} />
      <div style={{ position: 'absolute', left: 8, right: 8, top: -7, height: '80%', borderRadius: 8, background: '#dcdcd9', border: '1px solid #ebebea' }} />
      <Cover
        src={cover}
        fallback="#e8e8e6"
        radius={10}
        className="tr-hover-ring"
        style={{ position: 'absolute', inset: 0, border: '1px solid #ebebea', boxShadow: selected ? '0 0 0 2px #fff, 0 0 0 4px #0f0f10' : 'none' }}
      />
      {vis !== 'none' ? <VisBadge vis={vis} /> : null}
      <CountPill>
        {count} {count === 1 ? 'image' : 'images'}
      </CountPill>
      {onToggle ? (
        <div style={{ position: 'absolute', top: 10, left: 10 }}>
          <Checkbox checked={!!selected} onToggle={onToggle} size={20} raised label={selectLabel} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * The mark in front of a study's diagnosis: "Dx" in a small mono pill, like
 * the image count on a thumbnail. Read out as "Diagnosis".
 */
export function DxBadge() {
  return (
    <>
      <span aria-hidden style={{ display: 'inline-block', marginRight: 6, padding: '0 5px', borderRadius: 4, background: '#ececea', color: '#3f3f44', fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, fontWeight: 600, lineHeight: '16px', verticalAlign: 1 }}>
        Dx
      </span>
      <span className="sr-only">Diagnosis: </span>
    </>
  );
}

/**
 * Study Card.dc.html. Name in JetBrains Mono 14/500, description clamped to
 * two lines, then the diagnosis behind its Dx mark when there is one, also two
 * lines and darker and heavier than the description so the two are not
 * confused; meta (uploaded date or parent), optional Author Chip; comment
 * count and likes (hidden when `likes` is undefined).
 */
export function StudyCard({
  cover,
  count,
  vis,
  name,
  desc,
  diagnosis,
  meta,
  author,
  comments = 0,
  likes,
  liked,
  selected,
  onToggle,
  onOpen,
  onToggleLike,
}: {
  cover?: string;
  count: number;
  vis: Visibility | 'none';
  name: string;
  desc?: string;
  diagnosis?: string;
  meta?: string;
  author?: string;
  comments?: number;
  likes?: number;
  liked?: boolean;
  selected?: boolean;
  onToggle?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  onOpen: () => void;
  onToggleLike?: () => void;
}) {
  return (
    <div onClick={onOpen} style={{ display: 'flex', flexDirection: 'column', gap: 12, cursor: 'pointer' }}>
      <StackThumb cover={cover} count={count} vis={vis} selected={selected} onToggle={onToggle} selectLabel={`Select ${name}`} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500, fontFamily: "'JetBrains Mono', monospace" }}>{name}</div>
          <div className="tr-clamp-2" style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.4 }}>{desc}</div>
          {diagnosis ? (
            <div className="tr-clamp-2" style={{ fontSize: 12.5, fontWeight: 500, color: '#0f0f10', lineHeight: 1.4 }}>
              <DxBadge />
              {diagnosis}
            </div>
          ) : null}
          {meta ? <div className="tr-ellipsis" style={{ fontSize: 12, color: '#8a8a8f' }}>{meta}</div> : null}
          {author ? (
            <div style={{ marginTop: 3 }}>
              <AuthorChip author={author} />
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingTop: 2, flex: 'none' }}>
          <CommentCount count={comments} />
          {likes !== undefined ? <HeartCount likes={likes} liked={liked} onToggle={onToggleLike} /> : null}
        </div>
      </div>
    </div>
  );
}

/** The 72×48 stack used in study list rows and the existing-study picker (scaled). */
export function StackMini({ cover, selected }: { cover?: string; selected?: boolean }) {
  return (
    <div style={{ position: 'relative', width: 72, height: 48, marginTop: 6, flex: 'none' }}>
      <div style={{ position: 'absolute', left: 10, right: 10, top: -6, height: '70%', borderRadius: 4, background: '#ececea', border: '1px solid #ebebea' }} />
      <div style={{ position: 'absolute', left: 5, right: 5, top: -3, height: '80%', borderRadius: 4, background: '#dcdcd9', border: '1px solid #ebebea' }} />
      <Cover src={cover} fallback="#e8e8e6" radius={5} style={{ position: 'absolute', inset: 0, border: '1px solid #ebebea', boxShadow: selected ? '0 0 0 2px #fff, 0 0 0 4px #0f0f10' : undefined }} />
    </div>
  );
}
